/**
 * The recruiting slice of the wire, and the paths the recruiting screens call.
 *
 * ── Why this file exists next to `types.ts` and `paths.ts` ──────────────────
 *
 * `types.ts`'s own header sets the rule the whole client follows: transcribe the
 * slice a screen actually reads, not the 10 000-line contract, so there is never a
 * second full copy to keep in step. The recruiting module is the first surface of a
 * new area (requisitions, candidates, pipeline, interviews, offers), and its wire
 * shapes and its path builders are cohesive enough — and separate enough from the
 * workforce screens — that they earn their own file rather than being threaded
 * through the two shared ones. Later recruiting tasks bring the slice they need here.
 *
 * These types are **hand-written to mirror `openapi.yaml`** — the `RecruitingRequisition`,
 * `RecruitingPosting` and their request/response schemas (Phase 1 ATS, Task 7). They
 * are not generated from it and this task does not touch `openapi.yaml`; a field that
 * is `nullable` on the wire is `T | null` here, and a field the contract marks
 * optional is optional here, because `exactOptionalPropertyTypes` is on and an absent
 * value and a `null` one are different facts (`PRODUCT.md` §4).
 *
 * Nothing here authorises anything. `x-wp-permissions` on these endpoints is
 * `recruiting.view_jobs` (reads) and `recruiting.manage_jobs` (writes) at scope
 * `company`; the server runs that chain on every call, whatever the menu renders.
 */

import { qs, type QueryValue } from './paths';
import type { CursorListEnvelope } from './types';

/* ───────────────────────────────────────────────── requisitions (Task 7) ── */

/**
 * `RecruitingRequisitionStatus` — the requisition lifecycle CHECK (migration 029),
 * verbatim. The machine that moves between them lives in
 * `web/src/recruiting/requisition.ts`; this is only the vocabulary.
 */
export const REQUISITION_STATUSES = [
  'draft',
  'open',
  'on_hold',
  'filled',
  'cancelled',
  'closed',
] as const;

export type RequisitionStatus = (typeof REQUISITION_STATUSES)[number];

/** `RecruitingRequisition.employment_type` — `null` is a value: no type recorded. */
export const EMPLOYMENT_TYPES = ['full_time', 'part_time', 'contract', 'intern'] as const;

export type EmploymentType = (typeof EMPLOYMENT_TYPES)[number];

/**
 * `RecruitingRequisition` of `openapi.yaml`. `required: [id, title, headcount,
 * status, created_at, updated_at]`; every other member is nullable on the wire and
 * so `T | null` here rather than optional — the server sends the key with a `null`
 * value, it does not omit it.
 */
export interface RecruitingRequisition {
  readonly id: string;
  readonly title: string;
  readonly department_id: string | null;
  readonly hiring_manager_employee_id: string | null;
  /** The owning recruiter — what `owner=me` filters on. */
  readonly recruiter_employee_id: string | null;
  readonly employment_type: EmploymentType | null;
  readonly headcount: number;
  readonly location: string | null;
  readonly description: string | null;
  readonly status: RequisitionStatus;
  /** Stamped the first time the requisition enters `open`; server-owned. */
  readonly opened_at: string | null;
  /** Stamped when the requisition enters a terminal state; server-owned. */
  readonly closed_at: string | null;
  readonly created_by: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

/**
 * `RecruitingRequisitionCreateRequest`. Only `title` is required; `headcount`
 * defaults to 1 on the server and is omitted here when the form leaves it at 1.
 */
export interface RecruitingRequisitionCreateRequest {
  readonly title: string;
  readonly department_id?: string;
  readonly hiring_manager_employee_id?: string;
  readonly recruiter_employee_id?: string;
  readonly employment_type?: EmploymentType;
  readonly headcount?: number;
  readonly location?: string;
  readonly description?: string;
}

/**
 * `RecruitingRequisitionUpdateRequest` — a partial with `minProperties: 1`. A `status`
 * here is validated against the requisition state machine; an illegal move is `409
 * WORKFORCE_INVALID_STATE_TRANSITION` carrying `details.allowed_transitions[]`.
 *
 * The nullable fields accept `null` to **clear** them, which is a different request
 * from omitting the key (leave unchanged) — hence `T | null` on those, not `T`.
 */
export interface RecruitingRequisitionUpdateRequest {
  readonly title?: string;
  readonly department_id?: string | null;
  readonly hiring_manager_employee_id?: string | null;
  readonly recruiter_employee_id?: string | null;
  readonly employment_type?: EmploymentType | null;
  readonly headcount?: number;
  readonly location?: string | null;
  readonly description?: string | null;
  readonly status?: RequisitionStatus;
}

/** `RecruitingRequisitionListResponse` — a cursor page, newest first. */
export type RecruitingRequisitionListResponse = CursorListEnvelope<RecruitingRequisition>;

/* ─────────────────────────────────────────────────────── postings (Task 7) ── */

/** `RecruitingPosting.status` of `openapi.yaml`. */
export const POSTING_STATUSES = ['draft', 'published', 'unpublished', 'expired'] as const;

export type PostingStatus = (typeof POSTING_STATUSES)[number];

/** `RecruitingPosting.channel` — `null` is a value: no channel recorded. */
export const POSTING_CHANNELS = ['internal', 'career_site', 'referral', 'agency'] as const;

export type PostingChannel = (typeof POSTING_CHANNELS)[number];

/** `RecruitingPosting` of `openapi.yaml`. `required: [id, requisition_id, status, …]`. */
export interface RecruitingPosting {
  readonly id: string;
  readonly requisition_id: string;
  readonly channel: PostingChannel | null;
  readonly public_slug: string | null;
  readonly status: PostingStatus;
  readonly published_at: string | null;
  readonly expires_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

/**
 * `RecruitingPostingCreateRequest`. Every field optional; a posting is created in
 * `draft`. `public_slug` must match `^[a-z0-9-]+$` and be unique per company among
 * **published** postings — a collision answers `409 WORKFORCE_CONFLICT`.
 */
export interface RecruitingPostingCreateRequest {
  readonly channel?: PostingChannel;
  readonly public_slug?: string;
  readonly expires_at?: string;
}

/**
 * `RecruitingPostingListResponse` — **not** a paginated envelope. `openapi.yaml`
 * gives it a bare `{ data: [...] }` with no `page`, so this reads every posting of one
 * requisition, newest first, and there is no cursor to forward.
 */
export interface RecruitingPostingListResponse {
  readonly data: readonly RecruitingPosting[];
}

/* ─────────────────────────────────────────────────────────── path builders ── */

export const RECRUITING_REQUISITIONS_PATH = '/api/v1/recruiting/requisitions';

/**
 * `GET /recruiting/requisitions` — cursor-paged, scoped to the caller's active
 * company by the server and never by a query parameter (API_CONTRACT §7.4 rule 1).
 *
 * `status` and `owner` are the two server filters. `owner=me` narrows to the caller's
 * own requisitions; it is a row filter, not an authorization level. `cursor` is the
 * previous page's `page.next_cursor`, opaque and forwarded unread. Neither
 * `company_id` nor an `employee_id` is ever sent — the owner filter is a fixed `me`.
 */
export function recruitingRequisitionsPath(query: {
  readonly limit: number;
  readonly status?: RequisitionStatus | undefined;
  readonly owner?: 'me' | undefined;
  readonly cursor?: string | undefined;
}): string {
  const params: Readonly<Record<string, QueryValue>> = {
    limit: query.limit,
    status: query.status,
    owner: query.owner,
    cursor: query.cursor,
  };
  return `${RECRUITING_REQUISITIONS_PATH}${qs(params)}`;
}

/** `GET`/`PATCH /recruiting/requisitions/{id}` — one requisition by id. */
export function recruitingRequisitionPath(id: string): string {
  return `${RECRUITING_REQUISITIONS_PATH}/${encodeURIComponent(id)}`;
}

/**
 * `GET`/`POST /recruiting/requisitions/{id}/postings` — the postings of one
 * requisition. A requisition absent from the company answers `404`.
 */
export function recruitingPostingsPath(requisitionId: string): string {
  return `${recruitingRequisitionPath(requisitionId)}/postings`;
}

/* ───────────────────────────────────────────────── candidates (Task 8, WP-W2) ── */

/**
 * `RecruitingCandidate.source` of `openapi.yaml` — where the candidate came from.
 * `null` is a value on the wire (no origin recorded); the create request has no
 * `null` because omission is how "unset" is said there.
 */
export const CANDIDATE_SOURCES = ['career_site', 'referral', 'agency', 'manual'] as const;

export type CandidateSource = (typeof CANDIDATE_SOURCES)[number];

/**
 * `RecruitingCandidate` of `openapi.yaml`. `required: [id, full_name, created_at,
 * updated_at]`; every other member is nullable on the wire and so `T | null` here —
 * the server sends the key with a `null` value, it does not omit it. A **redacted**
 * candidate carries a non-null `redacted_at`, `full_name` reads `[redacted]` and the
 * three PII columns (`email`, `phone`, `linkedin_url`) are `null`.
 */
export interface RecruitingCandidate {
  readonly id: string;
  readonly full_name: string;
  readonly email: string | null;
  readonly phone: string | null;
  readonly source: CandidateSource | null;
  readonly current_title: string | null;
  readonly current_company: string | null;
  readonly location: string | null;
  readonly linkedin_url: string | null;
  /** When the candidate consented to their data being held. */
  readonly privacy_consent_at: string | null;
  /** Set by a right-to-erasure redaction; the PII columns are then blank. Server-owned. */
  readonly redacted_at: string | null;
  readonly created_by: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

/**
 * `RecruitingCandidateCreateRequest`. Only `full_name` is required; every other field
 * is present-or-absent (not nullable), so the form omits a blank rather than sending
 * an empty string — `exactOptionalPropertyTypes` makes those two different requests.
 */
export interface RecruitingCandidateCreateRequest {
  readonly full_name: string;
  readonly email?: string;
  readonly phone?: string;
  readonly source?: CandidateSource;
  readonly current_title?: string;
  readonly current_company?: string;
  readonly location?: string;
  readonly linkedin_url?: string;
  readonly privacy_consent_at?: string;
}

/**
 * `RecruitingCandidateUpdateRequest` — a partial with `minProperties: 1`. The nullable
 * fields accept `null` to **clear** them, which is a different request from omitting
 * the key (leave unchanged) — hence `T | null` on those.
 *
 * `redact: true` is the right-to-erasure action; it blanks the PII rather than editing
 * it, is idempotent, and the server ignores any editable field sent alongside it, so
 * this client sends `{ redact: true }` alone. `openapi.yaml` fixes it to the single
 * literal `true`, so it is typed `true` rather than `boolean` — `redact: false` is not
 * a request the contract has.
 */
export interface RecruitingCandidateUpdateRequest {
  readonly full_name?: string;
  readonly email?: string | null;
  readonly phone?: string | null;
  readonly source?: CandidateSource | null;
  readonly current_title?: string | null;
  readonly current_company?: string | null;
  readonly location?: string | null;
  readonly linkedin_url?: string | null;
  readonly privacy_consent_at?: string | null;
  readonly redact?: true;
}

/** `RecruitingCandidateListResponse` — a cursor page, newest first, redacted excluded. */
export type RecruitingCandidateListResponse = CursorListEnvelope<RecruitingCandidate>;

/* ───────────────────────────────────────── candidate documents (Task 8, WP-W2) ── */

/** `RecruitingCandidateDocument.doc_type` — `null` is a value on the wire. */
export const CANDIDATE_DOCUMENT_TYPES = ['cv', 'cover_letter', 'portfolio', 'other'] as const;

export type CandidateDocumentType = (typeof CANDIDATE_DOCUMENT_TYPES)[number];

/**
 * `RecruitingCandidateDocument` of `openapi.yaml` — a CV's **metadata**, never its
 * bytes and never a download URL. `required: [id, candidate_id, filename,
 * uploaded_at]`; the rest are nullable on the wire.
 */
export interface RecruitingCandidateDocument {
  readonly id: string;
  readonly candidate_id: string;
  readonly doc_type: CandidateDocumentType | null;
  readonly filename: string;
  readonly content_type: string | null;
  readonly file_size_bytes: number | null;
  readonly uploaded_by: string | null;
  readonly uploaded_at: string;
}

/**
 * `RecruitingCandidateDocumentRegisterRequest`. `filename` and `doc_type` are
 * required; `content_type` and `file_size_bytes` are present-or-absent and are filled
 * from the selected `File` (`file.type`, `file.size`) when the browser knows them.
 */
export interface RecruitingCandidateDocumentRegisterRequest {
  readonly filename: string;
  readonly doc_type: CandidateDocumentType;
  readonly content_type?: string;
  readonly file_size_bytes?: number;
}

/**
 * `RecruitingCandidateDocumentUpload` — the freshly registered document, and a
 * short-lived **presigned PUT URL** to upload its bytes to object storage directly.
 * The URL is a bearer credential (`openapi.yaml`: "Not logged; treat as a
 * credential"): it is spent immediately by a single `PUT` and never stored.
 */
export interface RecruitingCandidateDocumentUpload {
  readonly document: RecruitingCandidateDocument;
  readonly upload_url: string;
  readonly expires_at: string;
}

/**
 * `RecruitingCandidateDocumentListResponse` — a bare `{ data: [...] }`, newest first,
 * with **no** `page` and no cursor, so the documents of one candidate are read whole.
 */
export interface RecruitingCandidateDocumentListResponse {
  readonly data: readonly RecruitingCandidateDocument[];
}

/* ─────────────────────────────────────────────── candidate path builders (WP-W2) ── */

export const RECRUITING_CANDIDATES_PATH = '/api/v1/recruiting/candidates';

/**
 * `GET /recruiting/candidates` — cursor-paged, newest first, scoped to the caller's
 * active company by the server and never by a query parameter (API_CONTRACT §7.4
 * rule 1). Redacted candidates are excluded. `source` filters to one origin; `q` is a
 * case-insensitive substring match on `full_name` and `email`; `cursor` is the
 * previous page's `page.next_cursor`, opaque and forwarded unread.
 */
export function recruitingCandidatesPath(query: {
  readonly limit: number;
  readonly source?: CandidateSource | undefined;
  readonly q?: string | undefined;
  readonly cursor?: string | undefined;
}): string {
  const params: Readonly<Record<string, QueryValue>> = {
    limit: query.limit,
    source: query.source,
    q: query.q,
    cursor: query.cursor,
  };
  return `${RECRUITING_CANDIDATES_PATH}${qs(params)}`;
}

/** `GET`/`PATCH /recruiting/candidates/{id}` — one candidate by id. */
export function recruitingCandidatePath(id: string): string {
  return `${RECRUITING_CANDIDATES_PATH}/${encodeURIComponent(id)}`;
}

/**
 * `GET`/`POST /recruiting/candidates/{id}/documents` — the CV metadata of one
 * candidate (`GET`), and the register-and-get-a-presigned-PUT endpoint (`POST`). A
 * candidate absent from the company answers `404`.
 */
export function recruitingCandidateDocumentsPath(candidateId: string): string {
  return `${recruitingCandidatePath(candidateId)}/documents`;
}

/**
 * `GET /recruiting/candidates/{id}/documents/{docId}/download` — the one sensitive,
 * access-logged read of the recruiting surface. It requires
 * `recruiting.view_candidate_documents` and a mandatory `X-Access-Reason`, and `302`s
 * to a short-lived presigned GET URL that the browser follows.
 */
export function recruitingCandidateDocumentDownloadPath(
  candidateId: string,
  docId: string,
): string {
  return `${recruitingCandidateDocumentsPath(candidateId)}/${encodeURIComponent(docId)}/download`;
}

/* ─────────────────────────────────────── applications / pipeline (Task 9, WP-W3) ── */

/**
 * `RecruitingApplicationStage` of `openapi.yaml` — the pipeline stage of an
 * application. The stage machine that moves between them lives in
 * `web/src/recruiting/application.ts`; this is only the vocabulary, verbatim from the
 * contract's enum. `hired` is a member of the vocabulary but is **not reachable
 * through the transition endpoint** — it is the Phase 2 hire action's own target.
 */
export const APPLICATION_STAGES = [
  'applied',
  'screening',
  'interview',
  'offer',
  'hired',
  'rejected',
  'withdrawn',
] as const;

export type ApplicationStage = (typeof APPLICATION_STAGES)[number];

/**
 * `RecruitingApplication` of `openapi.yaml`. `required: [id, candidate_id,
 * requisition_id, stage, current_stage_since, created_at, updated_at]`; every other
 * member is nullable on the wire and so `T | null` here — the server sends the key
 * with a `null` value, it does not omit it. The application carries the two ids, not
 * the candidate's name or the requisition's title: those are resolved on the screen
 * from the candidate and requisition lists it already reads.
 */
export interface RecruitingApplication {
  readonly id: string;
  readonly candidate_id: string;
  readonly requisition_id: string;
  readonly posting_id: string | null;
  readonly stage: ApplicationStage;
  /** Set when the application is rejected; null otherwise. */
  readonly rejected_reason: string | null;
  /** When the application was created (entered `applied`). */
  readonly applied_at: string | null;
  /** When the application entered its current stage; server-owned. */
  readonly current_stage_since: string;
  readonly assigned_recruiter_employee_id: string | null;
  /** Optional recruiter rating; not set by the pipeline endpoints. */
  readonly rating: number | null;
  readonly created_at: string;
  readonly updated_at: string;
}

/**
 * `RecruitingApplicationCreateRequest`. `candidate_id` and `requisition_id` are
 * required; `posting_id` and `assigned_recruiter_employee_id` are present-or-absent
 * (omitted when blank, not sent empty). The application opens in stage `applied`.
 */
export interface RecruitingApplicationCreateRequest {
  readonly candidate_id: string;
  readonly requisition_id: string;
  readonly posting_id?: string;
  readonly assigned_recruiter_employee_id?: string;
}

/**
 * `RecruitingApplicationTransitionRequest` — the stage to move to, and an optional
 * note recorded on the history row. A `to_stage` of `hired` is refused `409` with a
 * hint to use the Phase 2 hire endpoint; this client never sends it (the stage
 * machine never offers it). An illegal move answers `409
 * WORKFORCE_INVALID_STATE_TRANSITION` carrying `details.allowed_transitions[]`.
 */
export interface RecruitingApplicationTransitionRequest {
  readonly to_stage: ApplicationStage;
  readonly note?: string;
}

/**
 * `RecruitingApplicationRejectRequest` — reject the application. The only field is an
 * optional reason, stamped on `rejected_reason` and recorded as the history row's
 * note. Omitted when blank, per the present-or-absent rule.
 */
export interface RecruitingApplicationRejectRequest {
  readonly reason?: string;
}

/** `RecruitingApplicationListResponse` — a cursor page, newest first. */
export type RecruitingApplicationListResponse = CursorListEnvelope<RecruitingApplication>;

/**
 * `RecruitingStageEvent` of `openapi.yaml` — one append-only stage-history row.
 * `required: [id, application_id, to_stage, created_at]`. `from_stage` is `null` for
 * the initial `applied` event; `moved_by` and `note` are nullable on the wire.
 */
export interface RecruitingStageEvent {
  readonly id: string;
  readonly application_id: string;
  readonly from_stage: string | null;
  readonly to_stage: string;
  readonly moved_by: string | null;
  readonly note: string | null;
  readonly created_at: string;
}

/**
 * `RecruitingStageEventListResponse` — a bare `{ data: [...] }`, **chronological
 * (oldest first)** with no `page` and no cursor, so the whole history of one
 * application is read at once.
 */
export interface RecruitingStageEventListResponse {
  readonly data: readonly RecruitingStageEvent[];
}

/* ──────────────────────────────────────────── application path builders (WP-W3) ── */

export const RECRUITING_APPLICATIONS_PATH = '/api/v1/recruiting/applications';

/**
 * `GET /recruiting/applications` — cursor-paged, newest first, scoped to the caller's
 * active company by the server and never by a query parameter (API_CONTRACT §7.4
 * rule 1). `requisition_id`, `candidate_id` and `stage` each narrow the list; `cursor`
 * is the previous page's `page.next_cursor`, opaque and forwarded unread.
 */
export function recruitingApplicationsPath(query: {
  readonly limit: number;
  readonly requisition_id?: string | undefined;
  readonly candidate_id?: string | undefined;
  readonly stage?: ApplicationStage | undefined;
  readonly cursor?: string | undefined;
}): string {
  const params: Readonly<Record<string, QueryValue>> = {
    limit: query.limit,
    requisition_id: query.requisition_id,
    candidate_id: query.candidate_id,
    stage: query.stage,
    cursor: query.cursor,
  };
  return `${RECRUITING_APPLICATIONS_PATH}${qs(params)}`;
}

/** `GET /recruiting/applications/{id}` — one application by id. */
export function recruitingApplicationPath(id: string): string {
  return `${RECRUITING_APPLICATIONS_PATH}/${encodeURIComponent(id)}`;
}

/** `POST /recruiting/applications/{id}/transition` — move one application's stage. */
export function recruitingApplicationTransitionPath(id: string): string {
  return `${recruitingApplicationPath(id)}/transition`;
}

/** `POST /recruiting/applications/{id}/reject` — reject one application, with a reason. */
export function recruitingApplicationRejectPath(id: string): string {
  return `${recruitingApplicationPath(id)}/reject`;
}

/** `GET /recruiting/applications/{id}/stage-events` — the append-only stage history. */
export function recruitingApplicationStageEventsPath(id: string): string {
  return `${recruitingApplicationPath(id)}/stage-events`;
}

/* ─────────────────────────── interviews, panel + scorecards (Task 10, WP-W4) ── */

/**
 * `RecruitingInterviewStatus` of `openapi.yaml` — the interview lifecycle. The status
 * machine that moves between them lives in `web/src/recruiting/interview.ts`; this is
 * only the vocabulary. `scheduled → {completed, cancelled, no_show}`; the last three
 * are terminal.
 */
export const INTERVIEW_STATUSES = ['scheduled', 'completed', 'cancelled', 'no_show'] as const;

export type InterviewStatus = (typeof INTERVIEW_STATUSES)[number];

/** `RecruitingInterviewKind` — the kind of interview. `null` is a value: none recorded. */
export const INTERVIEW_KINDS = ['phone', 'onsite', 'technical', 'panel', 'culture'] as const;

export type InterviewKind = (typeof INTERVIEW_KINDS)[number];

/** `RecruitingPanelRole` — an interviewer's role on the panel. `null` is a value. */
export const PANEL_ROLES = ['lead', 'panelist'] as const;

export type PanelRole = (typeof PANEL_ROLES)[number];

/** `RecruitingScorecardRecommendation` — the interviewer's hire recommendation. */
export const SCORECARD_RECOMMENDATIONS = ['strong_yes', 'yes', 'no', 'strong_no'] as const;

export type ScorecardRecommendation = (typeof SCORECARD_RECOMMENDATIONS)[number];

/**
 * `RecruitingInterview` of `openapi.yaml`. `required: [id, application_id, status,
 * created_at, updated_at]`; every other member is nullable on the wire and so `T | null`
 * here — the server sends the key with a `null` value, it does not omit it. The
 * interview carries neither its panel nor its scorecards: those are read (or written)
 * through their own sub-resources.
 */
export interface RecruitingInterview {
  readonly id: string;
  readonly application_id: string;
  readonly round_no: number | null;
  readonly kind: InterviewKind | null;
  readonly scheduled_start: string | null;
  readonly scheduled_end: string | null;
  readonly timezone: string | null;
  readonly location: string | null;
  readonly meeting_url: string | null;
  readonly status: InterviewStatus;
  readonly created_by: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

/**
 * `RecruitingPanelMember` of `openapi.yaml` — one interviewer seated on a panel.
 * `required: [id, interview_id, interviewer_employee_id, created_at, updated_at]`;
 * `panel_role` is nullable on the wire.
 */
export interface RecruitingPanelMember {
  readonly id: string;
  readonly interview_id: string;
  readonly interviewer_employee_id: string;
  readonly panel_role: PanelRole | null;
  readonly created_at: string;
  readonly updated_at: string;
}

/**
 * `RecruitingScorecard` of `openapi.yaml` — one interviewer's scorecard for an
 * interview. `required: [id, interview_id, interviewer_employee_id, created_at,
 * updated_at]`; the recorded ratings are nullable on the wire. One per
 * `(interview, interviewer)`; a resubmit updates the interviewer's own row. The
 * `interviewer_employee_id` is the session employee the server stamped — never chosen
 * in a form.
 */
export interface RecruitingScorecard {
  readonly id: string;
  readonly interview_id: string;
  readonly interviewer_employee_id: string;
  readonly recommendation: ScorecardRecommendation | null;
  readonly overall: number | null;
  /** An opaque per-competency map; the server does not fix its shape. */
  readonly competencies_json: Readonly<Record<string, unknown>> | null;
  readonly comments: string | null;
  readonly submitted_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

/**
 * `RecruitingInterviewScheduleRequest`. Every field is optional; any `panelists` are
 * seated in the same transaction as the interview. `round_no` is 1..32767; the string
 * fields are date-times (`scheduled_start`/`scheduled_end`) and free text. Present-or-
 * absent throughout — a blank is omitted, not sent empty.
 */
export interface RecruitingInterviewScheduleRequest {
  readonly round_no?: number;
  readonly kind?: InterviewKind;
  readonly scheduled_start?: string;
  readonly scheduled_end?: string;
  readonly timezone?: string;
  readonly location?: string;
  readonly meeting_url?: string;
  readonly panelists?: readonly RecruitingPanelistInput[];
}

/** One panelist to seat — `interviewer_employee_id` required, `panel_role` optional. */
export interface RecruitingPanelistInput {
  readonly interviewer_employee_id: string;
  readonly panel_role?: PanelRole;
}

/**
 * `RecruitingInterviewPatchRequest` — a partial with `minProperties: 1`. A `status`
 * here is validated against the interview status machine and may only be one of the
 * three terminal targets; an illegal move (including any move out of a terminal
 * status) answers `409 WORKFORCE_INVALID_STATE_TRANSITION` with the allowed list. The
 * reschedule fields are edited in place. None of these is nullable — the contract has
 * no clear-to-null here, only present (change) or absent (leave).
 */
export interface RecruitingInterviewPatchRequest {
  readonly status?: 'completed' | 'cancelled' | 'no_show';
  readonly scheduled_start?: string;
  readonly scheduled_end?: string;
  readonly timezone?: string;
  readonly location?: string;
  readonly meeting_url?: string;
}

/**
 * `RecruitingPanelMutateRequest` — add and/or remove panelists, `minProperties: 1`. At
 * least one of `add`/`remove` must be present; a re-add of an interviewer already
 * seated answers `409`. `remove` is a list of panelist ids. Returns the panel as it
 * stands afterwards.
 */
export interface RecruitingPanelMutateRequest {
  readonly add?: readonly RecruitingPanelistInput[];
  readonly remove?: readonly string[];
}

/**
 * `RecruitingScorecardSubmitRequest`. `recommendation` and `overall` (1..5) are
 * required; `competencies_json` and `comments` are optional. The interviewer is
 * **never** in the body — the server takes it from the session, so a caller cannot
 * submit as someone else.
 */
export interface RecruitingScorecardSubmitRequest {
  readonly recommendation: ScorecardRecommendation;
  readonly overall: number;
  readonly competencies_json?: Readonly<Record<string, unknown>>;
  readonly comments?: string;
}

/** `RecruitingInterviewListResponse` — a cursor page, newest first. */
export type RecruitingInterviewListResponse = CursorListEnvelope<RecruitingInterview>;

/**
 * `RecruitingPanelMemberListResponse` — a bare `{ data: [...] }` with no `page`: the
 * whole panel of one interview, as it stands after a mutate.
 */
export interface RecruitingPanelMemberListResponse {
  readonly data: readonly RecruitingPanelMember[];
}

/**
 * `RecruitingScorecardListResponse` — a bare `{ data: [...] }`, **oldest first**, with
 * no `page`: every scorecard of one interview, read whole.
 */
export interface RecruitingScorecardListResponse {
  readonly data: readonly RecruitingScorecard[];
}

/* ──────────────────────────────────────────── interview path builders (WP-W4) ── */

export const RECRUITING_INTERVIEWS_PATH = '/api/v1/recruiting/interviews';

/**
 * `POST /recruiting/applications/{id}/interviews` — schedule an interview against one
 * application. A requisition/application absent from the company answers `404`. The
 * interview opens in `scheduled`; `created_by` is the session employee.
 */
export function recruitingApplicationInterviewsPath(applicationId: string): string {
  return `${recruitingApplicationPath(applicationId)}/interviews`;
}

/**
 * `GET /recruiting/interviews` — cursor-paged, newest first, scoped to the caller's
 * active company by the server and never by a query parameter (API_CONTRACT §7.4
 * rule 1). `application_id` narrows to one application; `mine=true` narrows to the
 * interviews the session employee sits on the panel of (a panel-membership filter);
 * `cursor` is the previous page's `page.next_cursor`, opaque and forwarded unread.
 */
export function recruitingInterviewsPath(query: {
  readonly limit: number;
  readonly application_id?: string | undefined;
  readonly mine?: 'true' | undefined;
  readonly cursor?: string | undefined;
}): string {
  const params: Readonly<Record<string, QueryValue>> = {
    limit: query.limit,
    application_id: query.application_id,
    mine: query.mine,
    cursor: query.cursor,
  };
  return `${RECRUITING_INTERVIEWS_PATH}${qs(params)}`;
}

/** `GET`/`PATCH /recruiting/interviews/{id}` — one interview by id. */
export function recruitingInterviewPath(id: string): string {
  return `${RECRUITING_INTERVIEWS_PATH}/${encodeURIComponent(id)}`;
}

/** `POST /recruiting/interviews/{id}/panel` — add and/or remove panelists. */
export function recruitingInterviewPanelPath(id: string): string {
  return `${recruitingInterviewPath(id)}/panel`;
}

/**
 * `POST /recruiting/interviews/{id}/scorecard` — submit or update the **caller's own**
 * scorecard. The server refuses `403 WORKFORCE_SCOPE_DENIED` unless the session
 * employee is on the panel.
 */
export function recruitingInterviewScorecardPath(id: string): string {
  return `${recruitingInterviewPath(id)}/scorecard`;
}

/**
 * `GET /recruiting/interviews/{id}/scorecards` — every scorecard of one interview,
 * oldest first. Requires `recruiting.view_candidates` (a hiring manager reading the
 * panel's feedback), not `submit_feedback` (the interviewer filing it).
 */
export function recruitingInterviewScorecardsPath(id: string): string {
  return `${recruitingInterviewPath(id)}/scorecards`;
}

/* ─────────────────────────────────────────────────────── offers (WP-W5, Task 12) ── */

/**
 * `RecruitingOfferStatus` — the offer lifecycle enum of `openapi.yaml`, verbatim. The
 * machine that moves between them lives in `web/src/recruiting/offer.ts`; this is only
 * the vocabulary. The Phase 1 endpoints drive `approve`, `send` and `respond`;
 * `rescinded`/`expired` are legal statuses the machine carries for completeness and
 * have no Phase 1 write.
 */
export const OFFER_STATUSES = [
  'draft',
  'pending_approval',
  'approved',
  'sent',
  'accepted',
  'declined',
  'rescinded',
  'expired',
] as const;

export type OfferStatus = (typeof OFFER_STATUSES)[number];

/** `RecruitingOfferRespondRequest.outcome` — the candidate's decision on a sent offer. */
export const OFFER_RESPONSE_OUTCOMES = ['accepted', 'declined'] as const;

export type OfferResponseOutcome = (typeof OFFER_RESPONSE_OUTCOMES)[number];

/**
 * `RecruitingOffer` of `openapi.yaml`. `required: [id, application_id, status,
 * created_at, updated_at]`; every other member is nullable on the wire and so `T | null`
 * here rather than optional — the server sends the key with a `null` value, it does not
 * omit it. `salary_amount` is an exact decimal string (`NUMERIC(14,2)`), never a JS
 * number, so no cent is lost to a float. `approved_by`/`approved_at` are set together
 * when the offer is approved; `sent_at` when it is sent; `responded_at` when the
 * candidate responds.
 */
export interface RecruitingOffer {
  readonly id: string;
  readonly application_id: string;
  readonly status: OfferStatus;
  readonly salary_amount: string | null;
  readonly salary_currency: string | null;
  readonly start_date: string | null;
  readonly expires_at: string | null;
  readonly approved_by: string | null;
  readonly approved_at: string | null;
  readonly sent_at: string | null;
  readonly responded_at: string | null;
  readonly offer_document_s3_key: string | null;
  readonly created_by: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

/**
 * `RecruitingOfferCreateRequest`. Every field is optional — an empty body creates a
 * bare `draft` to be filled in and approved later — and `exactOptionalPropertyTypes`
 * makes an absent key and a `null` value different requests, so a blank field is
 * **omitted** rather than sent. `salary_amount` matches the wire pattern (up to 12
 * integer and 2 decimal digits); `salary_currency` is three uppercase letters.
 */
export interface RecruitingOfferCreateRequest {
  readonly salary_amount?: string;
  readonly salary_currency?: string;
  readonly start_date?: string;
  readonly expires_at?: string;
  readonly offer_document_s3_key?: string;
}

/**
 * `RecruitingOfferRespondRequest` — record the candidate's decision on a `sent` offer.
 * `outcome` is required. Recording `accepted` does not create an employee; the hire
 * hand-off is a later phase.
 */
export interface RecruitingOfferRespondRequest {
  readonly outcome: OfferResponseOutcome;
}

/**
 * `RecruitingOfferListResponse` — a bare `{ data: [...] }`, **newest first**, with no
 * `page`: every offer of one application, read whole. Offers list per application only;
 * there is no company-wide offers list.
 */
export interface RecruitingOfferListResponse {
  readonly data: readonly RecruitingOffer[];
}

/* ─────────────────────────────────────────────── offer path builders (WP-W5) ── */

export const RECRUITING_OFFERS_PATH = '/api/v1/recruiting/offers';

/**
 * `GET`/`POST /recruiting/applications/{id}/offers` — the offers of one application
 * (newest first) and the create of a fresh `draft` against it. An application absent
 * from the caller's active company answers `404`, not an empty list.
 */
export function recruitingApplicationOffersPath(applicationId: string): string {
  return `${recruitingApplicationPath(applicationId)}/offers`;
}

/**
 * `POST /recruiting/offers/{id}/approve` — move `{draft, pending_approval} → approved`,
 * stamping the approver server-side from the session. No request body.
 */
export function recruitingOfferApprovePath(id: string): string {
  return `${RECRUITING_OFFERS_PATH}/${encodeURIComponent(id)}/approve`;
}

/**
 * `POST /recruiting/offers/{id}/send` — move `approved → sent`, stamping `sent_at`. No
 * request body.
 */
export function recruitingOfferSendPath(id: string): string {
  return `${RECRUITING_OFFERS_PATH}/${encodeURIComponent(id)}/send`;
}

/**
 * `POST /recruiting/offers/{id}/respond` — move `sent → {accepted, declined}` per the
 * `outcome` in the body, stamping `responded_at`.
 */
export function recruitingOfferRespondPath(id: string): string {
  return `${RECRUITING_OFFERS_PATH}/${encodeURIComponent(id)}/respond`;
}
